"use strict";

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
(function () {
  // THE SIMULATION ENGINE: DEFIBRILLATION AND PACING. Shock outcomes, cardioversion, drug-driven
  // conversion, the device controls, Defib Sim custom sequences and transcutaneous pacing.
  // Called by useSimulation (data/engine.js) at a fixed point on every render, so its hooks keep
  // their order. It receives what it uses from the rest of the engine and returns what the rest
  // of the engine uses (both lists generated from a scope analysis of the original code).
  var _React = React,
    useState = _React.useState,
    useEffect = _React.useEffect,
    useRef = _React.useRef;
  var RG = window.RHYTHMS;
  var isDrugSpent = window.__EngineModel.isDrugSpent;
  var useDefib = function useDefib(ctx) {
    var RESET_HOLD_KEYS = ctx.RESET_HOLD_KEYS,
      addLogEntry = ctx.addLogEntry,
      arrestVitals = ctx.arrestVitals,
      changeRhythm = ctx.changeRhythm,
      clearSimTimer = ctx.clearSimTimer,
      deferUntilStart = ctx.deferUntilStart,
      dispatch = ctx.dispatch,
      isMonitorMode = ctx.isMonitorMode,
      notStarted = ctx.notStarted,
      simTimeout = ctx.simTimeout,
      state = ctx.state,
      stateRef = ctx.stateRef,
      triggerArrest = ctx.triggerArrest,
      triggerROSC = ctx.triggerROSC;

    // =====================================================================================
    // DEFIBRILLATION (C4 / C6 / C7) — one shared outcome helper, reached by:
    //   * the facilitator's arrest/defib panel (initCharge / deliverShock)
    //   * the 'Defib' intervention (effect.changeRhythm === 'defib')
    //   * a student pressing SHOCK on the monitor-hosted defib (Firebase deviceEvents)
    //   * a student pressing SHOCK on the standalone defib page (BroadcastChannel)
    // =====================================================================================
    var SHOCK_REFRACTORY_MS = 5000; // Stops charge/shock button-mashing maximising ROSC
    var refibTimerRef = useRef(null);
    var defibWeight = function defibWeight() {
      var _cur$scenario;
      var cur = stateRef.current;
      var w = Number((_cur$scenario = cur.scenario) === null || _cur$scenario === void 0 || (_cur$scenario = _cur$scenario.wetflag) === null || _cur$scenario === void 0 ? void 0 : _cur$scenario.weight);
      return Number.isFinite(w) && w > 0 ? w : null;
    };
    var recommendedShockEnergy = function recommendedShockEnergy() {
      var _cur$scenario2;
      var cur = stateRef.current;
      return RG.recommendedEnergy(defibWeight(), (_cur$scenario2 = cur.scenario) === null || _cur$scenario2 === void 0 ? void 0 : _cur$scenario2.patientAge);
    };
    var scheduleRefibrillation = function scheduleRefibrillation(fromRhythm) {
      var table = RG.SHOCK_OUTCOMES[RG.canonical(fromRhythm)];
      if (!table || !table.refibChance) return;
      if (Math.random() >= table.refibChance) return;
      // On the sim clock (paused with the scenario, cancelled by a new one), not the wall clock.
      clearSimTimer(refibTimerRef.current);
      var delay = 20 + Math.random() * 40;
      refibTimerRef.current = simTimeout(function () {
        refibTimerRef.current = null;
        var cur = stateRef.current;
        if (cur.isFinished) return;
        if (RG.isPulseless(cur.rhythm)) return; // already re-arrested another way
        dispatch({
          type: 'UPDATE_VITALS',
          payload: arrestVitals(cur.baseVitals)
        });
        changeRhythm(fromRhythm, 'refibrillation', {
          note: 're-arrest after ROSC'
        });
        dispatch({
          type: 'SET_FLASH',
          payload: 'red'
        });
      }, delay);
    };

    // ---- Shock-response settings (state.defibSettings). ----------------------------------
    var shockPolicy = function shockPolicy(cur) {
      return cur.defibSettings && cur.defibSettings.shockResponse || 'model';
    };
    // How many ADEQUATE shocks this rhythm needs before it converts, or null for "never".
    // 'auto' uses the scenario's own number when it sets one (scenario.shockToConvert).
    var shocksRequired = function shocksRequired(cur, isArrest) {
      var pol = shockPolicy(cur);
      if (pol === 'never') return null;
      if (/^[1-5]$/.test(pol)) return Number(pol);
      var sc = Number(cur.scenario && cur.scenario.shockToConvert);
      return Number.isFinite(sc) && sc > 0 ? sc : isArrest ? 3 : 1;
    };
    // Is `key` pharmacologically on board right now (a dose given and not yet worn off)?
    var drugOnBoard = function drugOnBoard(cur, key) {
      return (cur.activeDrugs || []).some(function (d) {
        return d.key === key && !isDrugSpent(d, cur.time);
      });
    };
    // Where a fixed-count shockable arrest converts to (scenario.successRhythm if it names one).
    var roscTargetFor = function roscTargetFor(cur, fromRhythm) {
      var named = cur.scenario && cur.scenario.successRhythm;
      if (named && RG.isKnown(named) && !RG.isPulseless(named)) return RG.canonical(named);
      var table = RG.SHOCK_OUTCOMES[RG.canonical(fromRhythm)] || RG.SHOCK_OUTCOMES['VF'];
      return RG.weightedPick(table.rosc);
    };
    // "VF recurs once": 30-90 s after ROSC, if the patient is still in the rhythm they converted to.
    var scheduleFixedRefib = function scheduleFixedRefib(convertedTo) {
      clearSimTimer(refibTimerRef.current);
      dispatch({
        type: 'SET_DEFIB_STATE',
        payload: {
          refibDone: true
        }
      });
      refibTimerRef.current = simTimeout(function () {
        refibTimerRef.current = null;
        var now = stateRef.current;
        if (!now || now.isFinished || RG.canonical(now.rhythm) !== RG.canonical(convertedTo)) return;
        dispatch({
          type: 'UPDATE_VITALS',
          payload: arrestVitals(now.baseVitals)
        });
        changeRhythm('VF', 'refibrillation', {
          note: 're-arrest after ROSC'
        });
        addLogEntry('VF has recurred after ROSC (refibrillation). Restart CPR and the shockable pathway.', 'danger', true);
        dispatch({
          type: 'SET_FLASH',
          payload: 'red'
        });
      }, 30 + Math.random() * 60);
    };
    var afterShockRosc = function afterShockRosc(cur, fromRhythm, convertedTo) {
      var refib = cur.defibSettings && cur.defibSettings.refib || 'model';
      if (refib === 'model') scheduleRefibrillation(fromRhythm);else if (refib === 'once' && !(cur.defib && cur.defib.refibDone)) scheduleFixedRefib(convertedTo);
    };

    // The ONE place a shock outcome is decided.
    function applyShockOutcome(cur) {
      var _cur$scenario3;
      var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var joules = Number.isFinite(Number(opts.energy)) ? Math.round(Number(opts.energy)) : recommendedShockEnergy();
      var sync = !!opts.sync;
      var source = opts.source || 'facilitator';
      var now = Date.now();
      var d = cur.defib || {};

      // --- Metrics (B5). EVERY delivered shock counts here, shockable or not, and these
      // numbers live in state so they reach Firebase, localStorage and the debrief.
      var nextDefib = {
        shockCount: (d.shockCount || 0) + 1,
        totalEnergy: (d.totalEnergy || 0) + joules,
        lastEnergy: joules,
        lastShockAt: now,
        charged: false,
        chargeEnergy: null
      };

      // --- C4: paediatric energy. Never blocked, always flagged (Wave 1 philosophy).
      // The shock number within this arrest (the reducer counts it when nextDefib lands).
      var shockNumber = (cur.arrest && cur.arrest.since !== null && cur.arrest.since !== undefined ? Number(cur.arrest.shocks) || 0 : 0) + 1;
      var dev = RG.energyDeviation(joules, defibWeight(), (_cur$scenario3 = cur.scenario) === null || _cur$scenario3 === void 0 ? void 0 : _cur$scenario3.patientAge, {
        shockNumber: shockNumber
      });
      if (dev) {
        var _cur$scenario4;
        addLogEntry("Shock energy deviation: ".concat(dev.reason, ". Recommended for this patient: ").concat(dev.expected, "J").concat(RG.isAdult(defibWeight(), (_cur$scenario4 = cur.scenario) === null || _cur$scenario4 === void 0 ? void 0 : _cur$scenario4.patientAge) ? '' : ' (4 J/kg)', "."), 'warning', true, {
          action: 'Defib',
          label: 'Defibrillation',
          missing: ["correct energy (".concat(dev.expected, "J)")]
        });
      }
      var shockable = RG.isShockable(cur.rhythm);

      // --- C7 FIX: the shock counter used to increment BEFORE this guard, so shocking a
      // non-shockable rhythm silently inflated the ROSC probability of the next real shock.
      if (!shockable) {
        dispatch({
          type: 'SET_DEFIB_STATE',
          payload: nextDefib
        });
        // An UNSYNCHRONISED shock into a rhythm WITH A PULSE can land on the T wave and
        // cause VF (R-on-T). Whether it does is the facilitator's setting.
        var perfusing = !RG.isPulseless(cur.rhythm) && RG.rWavePhase(cur.rhythm, cur.vitals && cur.vitals.hr, 0) !== null;
        var rOnT = cur.defibSettings && cur.defibSettings.rOnT || 'never';
        var causesVf = perfusing && !sync && (rOnT === 'always' || rOnT === 'sometimes' && Math.random() < 1 / 3);
        if (causesVf) {
          addLogEntry("UNSYNCHRONISED shock delivered into ".concat(RG.labelFor(cur.rhythm), " landed on the T wave \u2014 VF induced (R-on-T). A patient with a pulse needs SYNCHRONISED cardioversion."), 'danger', true, {
            action: 'Defib',
            label: 'Defibrillation',
            missing: ['synchronisation']
          });
          dispatch({
            type: 'UPDATE_VITALS',
            payload: arrestVitals(cur.baseVitals),
            releaseManual: RESET_HOLD_KEYS
          });
          changeRhythm('VF', 'defibrillation (R-on-T)', {
            energy: joules,
            sync: false
          });
          dispatch({
            type: 'SET_FLASH',
            payload: 'red'
          });
          return;
        }
        if (RG.isSyncCardiovertible(cur.rhythm) && !sync) {
          addLogEntry("Unsynchronised shock delivered into ".concat(RG.labelFor(cur.rhythm), " \u2014 this rhythm needs SYNCHRONISED cardioversion. Not blocked, but flagged."), 'warning', true, {
            action: 'Defib',
            label: 'Defibrillation',
            missing: ['synchronisation']
          });
        } else {
          addLogEntry("Shock delivered into non-shockable rhythm (".concat(RG.labelFor(cur.rhythm), ") \u2014 no effect. Check the rhythm before shocking."), 'warning', true, {
            action: 'Defib',
            label: 'Defibrillation',
            missing: ['a shockable rhythm']
          });
        }
        changeRhythm(cur.rhythm, 'defibrillation', {
          energy: joules,
          sync: sync,
          note: 'non-shockable, no change'
        });
        return;
      }
      if (sync) {
        addLogEntry("SYNCHRONISED shock delivered into ".concat(RG.labelFor(cur.rhythm), " \u2014 a pulseless rhythm has no R wave to synchronise to, so the device would not fire in sync. Treat as unsynchronised. Flagged."), 'warning', true, {
          action: 'Defib',
          label: 'Defibrillation',
          missing: ['unsynchronised mode']
        });
      }
      nextDefib.shockableShocks = (d.shockableShocks || 0) + 1;

      // --- C7: refractory period. A shock stacked on top of the previous one within 5s is
      // still delivered and still logged, but earns no new physiological roll.
      var stacked = d.lastShockAt && now - d.lastShockAt < SHOCK_REFRACTORY_MS;
      if (stacked) {
        nextDefib.shockableShocks = d.shockableShocks || 0; // does not advance the ROSC ladder
        dispatch({
          type: 'SET_DEFIB_STATE',
          payload: nextDefib
        });
        addLogEntry("Second shock delivered ".concat(Math.round((now - d.lastShockAt) / 1000), "s after the last one \u2014 stacked shocks give no additional benefit. Two minutes of good CPR between shocks is the intervention. Flagged."), 'warning', true, {
          action: 'Defib',
          label: 'Defibrillation',
          missing: ['2 minutes of CPR between shocks']
        });
        changeRhythm(cur.rhythm, 'defibrillation', {
          energy: joules,
          sync: false,
          note: 'stacked shock, no change'
        });
        return;
      }
      var fromRhythm = RG.canonical(cur.rhythm);
      var policy = shockPolicy(cur);

      // --- FIXED-COUNT POLICIES ('auto', '1'-'5', 'never'): an adequate shock advances the
      // episode count, and the rhythm converts when the count is reached.
      if (policy !== 'model') {
        var _cur$scenario5;
        var adequate = RG.adequateShock(joules, defibWeight(), (_cur$scenario5 = cur.scenario) === null || _cur$scenario5 === void 0 ? void 0 : _cur$scenario5.patientAge, 'arrest');
        if (!adequate) {
          var _cur$scenario6;
          dispatch({
            type: 'SET_DEFIB_STATE',
            payload: nextDefib
          });
          changeRhythm(cur.rhythm, 'defibrillation', {
            energy: joules,
            sync: false,
            note: 'energy too low to defibrillate — resume CPR'
          });
          addLogEntry("Shock at ".concat(joules, "J is below the energy needed to defibrillate this patient (").concat(RG.isAdult(defibWeight(), (_cur$scenario6 = cur.scenario) === null || _cur$scenario6 === void 0 ? void 0 : _cur$scenario6.patientAge) ? 'at least 150 J' : 'at least 3 J/kg', "). Resume CPR and select a higher energy."), 'warning', true, {
            action: 'Defib',
            label: 'Defibrillation',
            missing: ['adequate energy']
          });
          return;
        }
        nextDefib.episodeShocks = (d.episodeShocks || 0) + 1;
        dispatch({
          type: 'SET_DEFIB_STATE',
          payload: nextDefib
        });
        if (cur.queuedRhythm) {
          var q = RG.canonical(cur.queuedRhythm);
          dispatch({
            type: 'SET_QUEUED_RHYTHM',
            payload: null
          });
          if (RG.isPulseless(q)) {
            dispatch({
              type: 'UPDATE_VITALS',
              payload: arrestVitals(cur.baseVitals)
            });
            changeRhythm(q, 'defibrillation (facilitator override)', {
              energy: joules
            });
          } else {
            triggerROSC(q, 'defibrillation (facilitator override)', {
              energy: joules
            });
            afterShockRosc(cur, fromRhythm, q);
          }
          return;
        }
        var required = shocksRequired(cur, true);
        if (required !== null && nextDefib.episodeShocks >= required) {
          var target = roscTargetFor(cur, fromRhythm);
          triggerROSC(target, 'defibrillation', {
            energy: joules,
            sync: false
          });
          afterShockRosc(cur, fromRhythm, target);
        } else {
          changeRhythm(cur.rhythm, 'defibrillation', {
            energy: joules,
            sync: false,
            note: 'no change — resume CPR'
          });
          addLogEntry('No change after shock. Resume compressions immediately, 2-minute cycle, consider escalating energy.', 'warning');
        }
        return;
      }
      dispatch({
        type: 'SET_DEFIB_STATE',
        payload: nextDefib
      });

      // --- FACILITATOR OVERRIDE (C7): "next shock converts to X". Uses the previously
      // unreachable queuedRhythm / SET_QUEUED_RHYTHM code, which is now driven by a real
      // control on the assessor's defib panel.
      if (cur.queuedRhythm) {
        var _q = RG.canonical(cur.queuedRhythm);
        dispatch({
          type: 'SET_QUEUED_RHYTHM',
          payload: null
        });
        if (RG.isPulseless(_q)) {
          dispatch({
            type: 'UPDATE_VITALS',
            payload: arrestVitals(cur.baseVitals)
          });
          changeRhythm(_q, 'defibrillation (facilitator override)', {
            energy: joules
          });
        } else {
          triggerROSC(_q, 'defibrillation (facilitator override)', {
            energy: joules
          });
          afterShockRosc(cur, cur.rhythm, _q); // honours "After ROSC" like every other shock
        }
        return;
      }

      // --- C7: energy-, rhythm-, CPR- and drug-sensitive ROSC probability.
      var shocks = nextDefib.shockableShocks;
      var expected = recommendedShockEnergy();
      // Under-dosing genuinely reduces defibrillation success; over-dosing does not help.
      var energyFactor = Math.max(0.4, Math.min(1.1, joules / Math.max(1, expected)));
      var rhythmFactor = RG.canonical(cur.rhythm) === 'Fine VF' ? 0.6 : 1.0; // fine VF defibrillates poorly
      var cprBonus = cur.cprInProgress ? 0.10 : 0;
      var drugBonus = Math.min(0.15, Number(d.shockBonus) || 0);
      var base = 0.08 + 0.07 * Math.min(shocks, 5);
      var roscChance = Math.max(0.02, Math.min(0.55, (base + cprBonus + drugBonus) * energyFactor * rhythmFactor));
      var table = RG.SHOCK_OUTCOMES[fromRhythm] || RG.SHOCK_OUTCOMES['VF'];
      if (Math.random() < roscChance) {
        var _target = RG.weightedPick(table.rosc);
        // Banked drug bonus is consumed by a successful shock.
        dispatch({
          type: 'SET_DEFIB_STATE',
          payload: {
            shockBonus: 0
          }
        });
        triggerROSC(_target, 'defibrillation', {
          energy: joules,
          sync: false
        });
        afterShockRosc(cur, fromRhythm, _target);
      } else {
        var _target2 = RG.weightedPick(table.noRosc);
        if (_target2 !== fromRhythm) {
          dispatch({
            type: 'UPDATE_VITALS',
            payload: arrestVitals(cur.baseVitals)
          });
        }
        changeRhythm(_target2, 'defibrillation', {
          energy: joules,
          sync: false,
          note: _target2 === fromRhythm ? 'no change — resume CPR' : 'post-shock rhythm change'
        });
        if (_target2 === fromRhythm) {
          addLogEntry('No change after shock. Resume compressions immediately, 2-minute cycle, consider escalating energy.', 'warning');
        }
      }
    }

    // --- C6: SYNCHRONISED CARDIOVERSION. `toggleSync` used to only flip a flag; the flag was
    // transmitted and then DISCARDED engine-side, and the 'Cardioversion' intervention's
    // changeRhythm:'sync' was never handled at all.
    function applyCardioversion(cur) {
      var _cur$scenario7, _cur$scenario9;
      var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var joules = Number.isFinite(Number(opts.energy)) ? Math.round(Number(opts.energy)) : recommendedShockEnergy();
      var d = cur.defib || {};
      var now = Date.now();
      var nextDefib = {
        shockCount: (d.shockCount || 0) + 1,
        totalEnergy: (d.totalEnergy || 0) + joules,
        lastEnergy: joules,
        lastShockAt: now,
        charged: false,
        chargeEnergy: null,
        syncMode: true
      };
      var dev = RG.energyDeviation(joules, defibWeight(), (_cur$scenario7 = cur.scenario) === null || _cur$scenario7 === void 0 ? void 0 : _cur$scenario7.patientAge, {
        kind: 'cardiovert',
        rhythm: cur.rhythm
      });
      if (dev && !RG.isPulseless(cur.rhythm)) addLogEntry("Cardioversion energy deviation: ".concat(dev.reason, ". Recommended first shock: ").concat(dev.expected, "J."), 'warning', true, {
        action: 'Cardioversion',
        label: 'Synchronised Cardioversion',
        missing: ["correct energy (".concat(dev.expected, "J)")]
      });
      if (RG.isPulseless(cur.rhythm)) {
        dispatch({
          type: 'SET_DEFIB_STATE',
          payload: nextDefib
        });
        // Never blocked — flagged. A defibrillator in SYNC mode will not discharge into VF,
        // and that is itself the teaching point.
        addLogEntry("SYNC mode armed in ".concat(RG.labelFor(cur.rhythm), " \u2014 a real defibrillator will not discharge in SYNC without an R wave. Switch to unsynchronised defibrillation. Flagged."), 'danger', true, {
          action: 'Cardioversion',
          label: 'Synchronised Cardioversion',
          missing: ['unsynchronised mode for a pulseless rhythm']
        });
        changeRhythm(cur.rhythm, 'cardioversion', {
          energy: joules,
          sync: true,
          note: 'no R wave, device would not fire'
        });
        return;
      }
      if (!RG.isSyncCardiovertible(cur.rhythm)) {
        dispatch({
          type: 'SET_DEFIB_STATE',
          payload: nextDefib
        });
        addLogEntry("Synchronised shock delivered into ".concat(RG.labelFor(cur.rhythm), " \u2014 cardioversion is not indicated for this rhythm. Flagged."), 'warning', true, {
          action: 'Cardioversion',
          label: 'Synchronised Cardioversion',
          missing: ['an indication for cardioversion']
        });
        changeRhythm(cur.rhythm, 'cardioversion', {
          energy: joules,
          sync: true,
          note: 'not indicated, no change'
        });
        return;
      }

      // The rhythm FIRST (it brings the new rhythm's rate band), then any obs the scenario names.
      // In the other order the rhythm change, which reads the pre-shock state, put the
      // tachycardic rate back: "Sinus Rhythm" at 190/min.
      var convertToSinus = function convertToSinus() {
        var sv = cur.scenario && cur.scenario.successVitals;
        var named = cur.scenario && cur.scenario.successRhythm;
        var target = named && RG.isKnown(named) && !RG.isPulseless(named) ? RG.canonical(named) : 'Sinus Rhythm';
        changeRhythm(target, 'cardioversion', {
          energy: joules,
          sync: true
        });
        if (sv && _typeof(sv) === 'object') {
          var vit = {};
          ['hr', 'bpSys', 'bpDia', 'spO2', 'rr', 'etco2'].forEach(function (k) {
            if (Number.isFinite(Number(sv[k]))) vit[k] = Number(sv[k]);
          });
          if (Object.keys(vit).length) dispatch({
            type: 'UPDATE_VITALS',
            payload: vit
          });
        }
      };

      // The facilitator's chosen conversion energy, if set: below it a synchronised shock never
      // converts; at or above it the shock counts (and with the realistic model it converts).
      var cvMin = Number(cur.defibSettings && cur.defibSettings.cvEnergy) || null;
      if (shockPolicy(cur) !== 'model' || cvMin) {
        var _cur$scenario8;
        var adequate = cvMin ? joules >= cvMin : RG.adequateShock(joules, defibWeight(), (_cur$scenario8 = cur.scenario) === null || _cur$scenario8 === void 0 ? void 0 : _cur$scenario8.patientAge, 'cardiovert');
        if (!adequate) {
          dispatch({
            type: 'SET_DEFIB_STATE',
            payload: nextDefib
          });
          changeRhythm(cur.rhythm, 'cardioversion', {
            energy: joules,
            sync: true,
            note: cvMin ? 'unsuccessful — escalate the energy' : 'energy too low to cardiovert'
          });
          return;
        }
        nextDefib.episodeShocks = (d.episodeShocks || 0) + 1;
        dispatch({
          type: 'SET_DEFIB_STATE',
          payload: nextDefib
        });
        var required = shockPolicy(cur) === 'model' ? 1 : shocksRequired(cur, false);
        if (required !== null && nextDefib.episodeShocks >= required) convertToSinus();else changeRhythm(cur.rhythm, 'cardioversion', {
          energy: joules,
          sync: true,
          note: 'unsuccessful — escalate energy, check sedation and synchronisation'
        });
        return;
      }
      dispatch({
        type: 'SET_DEFIB_STATE',
        payload: nextDefib
      });
      // Success depends on the rhythm and on adequate energy.
      var baseSuccess = {
        'SVT': 0.85,
        'VT': 0.80,
        'AF': 0.6,
        'Atrial Flutter': 0.9
      }[RG.canonical(cur.rhythm)] || 0.7;
      var expected = RG.cardioversionEnergy(defibWeight(), (_cur$scenario9 = cur.scenario) === null || _cur$scenario9 === void 0 ? void 0 : _cur$scenario9.patientAge, cur.rhythm);
      var energyFactor = Math.max(0.5, Math.min(1.1, joules / Math.max(1, expected)));
      if (Math.random() < baseSuccess * energyFactor) {
        convertToSinus();
      } else {
        changeRhythm(cur.rhythm, 'cardioversion', {
          energy: joules,
          sync: true,
          note: 'unsuccessful — escalate energy, check sedation and synchronisation'
        });
      }
    }

    // --- C6: DRUG-MEDIATED CONVERSION. `changeRhythm: 'chance'` was unhandled, so Adrenaline IV
    // and Amiodarone — the two most important drugs in a shockable arrest — changed NOTHING.
    function applyDrugConversion(cur, key, label) {
      var opts = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
      var table = RG.DRUG_CONVERSION[key];
      var rid = RG.canonical(cur.rhythm);
      var rule = table && table[rid];
      // Adenosine's success probability escalates with the 6 → 12 → 18 mg sequence, so the
      // caller may supply the chance for THIS dose. The registry still owns the target rhythm.
      if (rule && Number.isFinite(opts.chance)) rule = _objectSpread(_objectSpread({}, rule), {}, {
        chance: opts.chance
      });
      if (!rule) {
        addLogEntry("".concat(label, " given in ").concat(RG.labelFor(rid), " \u2014 no direct rhythm effect expected for this combination."), 'info');
        return;
      }
      if (rule.shockBonus) {
        var d = cur.defib || {};
        dispatch({
          type: 'SET_DEFIB_STATE',
          payload: {
            shockBonus: Math.min(0.15, (Number(d.shockBonus) || 0) + rule.shockBonus)
          }
        });
        addLogEntry("".concat(label, " on board \u2014 improves the chance that the NEXT shock is successful (").concat(Math.round(rule.shockBonus * 100), "% added)."), 'info');
      }
      if (!rule.chance || !rule.to) return;
      if (Math.random() < rule.chance) {
        if (RG.isPulseless(rule.to)) {
          dispatch({
            type: 'UPDATE_VITALS',
            payload: arrestVitals(cur.baseVitals)
          });
          changeRhythm(rule.to, 'drug', {
            agent: label
          });
        } else if (RG.isPulseless(rid)) {
          triggerROSC(rule.to, 'drug', {
            agent: label
          });
        } else {
          changeRhythm(rule.to, 'drug', {
            agent: label
          });
        }
      } else {
        addLogEntry("".concat(label, " given \u2014 rhythm unchanged (").concat(RG.labelFor(rid), ")."), 'info');
      }
    }
    function initCharge(energy) {
      var _cur$defib;
      var cur = stateRef.current;
      var j = Number.isFinite(Number(energy)) ? Math.round(Number(energy)) : recommendedShockEnergy();
      dispatch({
        type: 'SET_FLASH',
        payload: 'yellow'
      });
      dispatch({
        type: 'SET_DEFIB_STATE',
        payload: {
          charged: true,
          chargeEnergy: j,
          energy: j
        }
      });
      addLogEntry("Defib charging (".concat(j, "J").concat((_cur$defib = cur.defib) !== null && _cur$defib !== void 0 && _cur$defib.syncMode ? ', SYNC' : '', ")"), 'warning');
      dispatch({
        type: 'SET_NOTIFICATION',
        payload: {
          msg: "Charging ".concat(j, "J..."),
          type: 'warning',
          id: Date.now()
        }
      });
      setTimeout(function () {
        return dispatch({
          type: 'SET_FLASH',
          payload: null
        });
      }, 1000);
    }
    function deliverShock(energy) {
      var source = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'facilitator';
      var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      var cur = stateRef.current;
      var j = Number.isFinite(Number(energy)) ? Math.round(Number(energy)) : recommendedShockEnergy();
      var sync = opts.sync !== undefined ? !!opts.sync : !!(cur.defib && cur.defib.syncMode);
      dispatch({
        type: 'SET_FLASH',
        payload: 'red'
      });
      // Logged as 'danger' AND flagged, and the debrief now plots danger-type markers.
      addLogEntry("Shock delivered ".concat(j, "J").concat(sync ? ' (SYNC)' : '', " (").concat(source, ")"), 'danger', true);
      dispatch({
        type: 'SET_NOTIFICATION',
        payload: {
          msg: "Shock Delivered ".concat(j, "J"),
          type: 'danger',
          id: Date.now()
        }
      });
      setTimeout(function () {
        return dispatch({
          type: 'SET_FLASH',
          payload: null
        });
      }, 500);
      // Before START the patient is not live: the shock is recorded now and reaches the
      // patient (rhythm outcome, shock tally) when START is pressed, like a drug given early.
      var outcome = function outcome(now) {
        if (stepsRunning(now)) {
          customStepShock(now, j, sync);
          return;
        }
        if (sync) applyCardioversion(now, {
          energy: j,
          source: source
        });else applyShockOutcome(now, {
          energy: j,
          sync: false,
          source: source
        });
      };
      if (notStarted && notStarted(cur)) {
        deferUntilStart(sync ? 'Cardioversion' : 'Defib', "".concat(sync ? 'synchronised shock' : 'shock', " ").concat(j, "J"), outcome);
        addLogEntry("Shock ".concat(j, "J recorded before START: the patient is not live yet, so its effect is applied when START is pressed."), 'info');
        return;
      }
      outcome(cur);
    }

    // Assessor-facing defib device controls, shared by the controller panel and by student
    // events arriving over Firebase / BroadcastChannel.
    var setDefibMode = function setDefibMode(mode) {
      var source = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'facilitator';
      dispatch({
        type: 'SET_DEFIB_STATE',
        payload: {
          mode: mode,
          charged: false,
          chargeEnergy: null
        }
      });
      addLogEntry("Defibrillator mode: ".concat(String(mode).toUpperCase(), " (").concat(source, ")"), 'action');
    };
    var setDefibEnergy = function setDefibEnergy(j) {
      var source = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'facilitator';
      var v = Math.max(1, Math.round(Number(j) || 0));
      dispatch({
        type: 'SET_DEFIB_STATE',
        payload: {
          energy: v,
          charged: false,
          chargeEnergy: null
        }
      });
      addLogEntry("Energy selected: ".concat(v, "J (").concat(source, ")"), 'action');
    };
    var toggleDefibSync = function toggleDefibSync() {
      var source = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'facilitator';
      var cur = stateRef.current;
      var next = !(cur.defib && cur.defib.syncMode);
      dispatch({
        type: 'SET_DEFIB_STATE',
        payload: {
          syncMode: next
        }
      });
      addLogEntry("SYNC ".concat(next ? 'ON' : 'OFF', " (").concat(source, ")").concat(next && RG.isPulseless(cur.rhythm) ? ' — SYNC armed in a pulseless rhythm; the device will not discharge. Flagged.' : ''), next && RG.isPulseless(cur.rhythm) ? 'warning' : 'action', next && RG.isPulseless(cur.rhythm));
    };
    var analyseRhythm = function analyseRhythm() {
      var source = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'student';
      var cur = stateRef.current;
      var shockable = RG.isShockable(cur.rhythm);
      var result = shockable ? 'SHOCK ADVISED' : 'NO SHOCK ADVISED';
      dispatch({
        type: 'SET_DEFIB_STATE',
        payload: {
          analysing: false,
          lastAnalysis: {
            result: result,
            rhythm: RG.canonical(cur.rhythm),
            at: Date.now()
          }
        }
      });
      addLogEntry("Defib analysis (".concat(source, "): ").concat(result, " \u2014 ").concat(RG.labelFor(cur.rhythm)), 'action');
      defibStepTrigger('analyse');
      return result;
    };
    var setQueuedRhythm = function setQueuedRhythm(r) {
      if (!r) {
        dispatch({
          type: 'SET_QUEUED_RHYTHM',
          payload: null
        });
        addLogEntry('Facilitator override cleared: next shock follows the model.', 'system');
        return;
      }
      var q = RG.canonical(r);
      dispatch({
        type: 'SET_QUEUED_RHYTHM',
        payload: q
      });
      addLogEntry("Facilitator override armed: the NEXT shock will convert to ".concat(RG.labelFor(q), "."), 'system');
    };
    var toggleCPR = function toggleCPR(on) {
      var source = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'facilitator';
      var cur = stateRef.current;
      var next = on === undefined ? !cur.cprInProgress : !!on;
      if (next === cur.cprInProgress) return;
      dispatch({
        type: 'TOGGLE_CPR',
        payload: next
      });
      if (next) dispatch({
        type: 'RESET_CYCLE_TIMER'
      });
      addLogEntry(next ? "CPR started (".concat(source, ") \u2014 arrest ETCO2, compression artefact and the ROSC bonus are now active. Aim for 100-120/min, minimise pauses.") : "CPR stopped (".concat(source, ")."), next ? 'action' : 'warning', !next && RG.isPulseless(cur.rhythm));
    };

    // =====================================================================================
    // DEFIB SIM CUSTOM SCENARIOS. The facilitator's sequence of rhythms decides what happens:
    // each step moves on at its trigger (a rhythm analysis, a shock, pacing capture or a
    // timer). While a sequence runs, a shock never converts the rhythm by itself — only a
    // step whose trigger is "shock" moves on (and a shock on the LAST such step converts to
    // sinus rhythm), exactly as in the standalone Defib-sim.
    // =====================================================================================
    var DEFIB_TRIGGER_LABELS = {
      analyse: 'on analyse',
      shock: 'on shock',
      capture: 'on pacing capture',
      timer_30: 'after 30 s',
      timer_60: 'after 60 s',
      timer_120: 'after 2 min'
    };
    var defibSteps = function defibSteps(cur) {
      return cur.scenario && cur.scenario.defibSim && Array.isArray(cur.scenario.defibSim.steps) ? cur.scenario.defibSim.steps : [];
    };
    var stepsRunning = function stepsRunning(cur) {
      return !!(cur.defibStep && !cur.defibStep.done && defibSteps(cur).length);
    };
    var goToDefibStep = function goToDefibStep(index) {
      var cause = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'facilitator';
      var cur = stateRef.current;
      var steps = defibSteps(cur);
      if (!steps.length) return;
      var i = Math.max(0, Math.floor(Number(index) || 0));
      if (i >= steps.length) {
        dispatch({
          type: 'SET_DEFIB_STEP',
          payload: {
            index: steps.length,
            since: Number(cur.time) || 0,
            done: true
          }
        });
        addLogEntry("Custom scenario complete (".concat(cause, ")."), 'success');
        return;
      }
      var step = steps[i];
      var to = RG.canonical(step.rhythm);
      dispatch({
        type: 'SET_DEFIB_STEP',
        payload: {
          index: i,
          since: Number(cur.time) || 0,
          done: false
        }
      });
      addLogEntry("Custom scenario: step ".concat(i + 1, "/").concat(steps.length, " \u2014 ").concat(RG.labelFor(to), " (moves on ").concat(DEFIB_TRIGGER_LABELS[step.trigger] || step.trigger, "; ").concat(cause, ")"), 'system');
      if (to === RG.canonical(cur.rhythm)) return;
      if (RG.isPulseless(to) && !RG.isPulseless(cur.rhythm)) triggerArrest(to, "custom scenario step ".concat(i + 1));else if (!RG.isPulseless(to) && RG.isPulseless(cur.rhythm)) triggerROSC(to, "custom scenario step ".concat(i + 1));else changeRhythm(to, "custom scenario step ".concat(i + 1));
    };
    var defibStepTrigger = function defibStepTrigger(kind) {
      var cur = stateRef.current;
      if (!stepsRunning(cur)) return;
      var step = defibSteps(cur)[cur.defibStep.index];
      if (step && step.trigger === kind) goToDefibStep(cur.defibStep.index + 1, DEFIB_TRIGGER_LABELS[kind] || kind);
    };
    // A shock while a sequence runs: counted, logged, and it moves the sequence on only when the
    // current step says so.
    var customStepShock = function customStepShock(cur, joules, sync) {
      var d = cur.defib || {};
      dispatch({
        type: 'SET_DEFIB_STATE',
        payload: {
          shockCount: (d.shockCount || 0) + 1,
          totalEnergy: (d.totalEnergy || 0) + joules,
          lastEnergy: joules,
          lastShockAt: Date.now(),
          charged: false,
          chargeEnergy: null
        }
      });
      var steps = defibSteps(cur);
      var index = cur.defibStep.index;
      var step = steps[index];
      if (!step || step.trigger !== 'shock') {
        changeRhythm(cur.rhythm, 'defibrillation', {
          energy: joules,
          sync: sync,
          note: 'no change (custom scenario step)'
        });
        return;
      }
      setTimeout(function () {
        var now = stateRef.current;
        if (!now.defibStep || now.defibStep.done || now.defibStep.index !== index) return;
        if (index + 1 >= steps.length) {
          dispatch({
            type: 'SET_DEFIB_STEP',
            payload: {
              index: steps.length,
              since: Number(now.time) || 0,
              done: true
            }
          });
          if (RG.isPulseless(now.rhythm)) triggerROSC('Sinus Rhythm', 'defibrillation (custom scenario)', {
            energy: joules
          });else changeRhythm('Sinus Rhythm', 'cardioversion (custom scenario)', {
            energy: joules,
            sync: sync
          });
          addLogEntry('Custom scenario complete: the final shock converted the rhythm.', 'success');
          return;
        }
        goToDefibStep(index + 1, 'on shock');
      }, 1000);
    };
    // Timer steps run on the sim clock, so they pause with the scenario.
    useEffect(function () {
      if (isMonitorMode) return;
      var cur = stateRef.current;
      if (!stepsRunning(cur) || !cur.isRunning) return;
      var step = defibSteps(cur)[cur.defibStep.index];
      var m = /^timer_(\d+)$/.exec(step && step.trigger || '');
      if (m && (Number(cur.time) || 0) - (Number(cur.defibStep.since) || 0) >= Number(m[1])) goToDefibStep(cur.defibStep.index + 1, DEFIB_TRIGGER_LABELS[step.trigger]);
    }, [isMonitorMode, state.time]);

    // =====================================================================================
    // TRANSCUTANEOUS PACING, AS THE DEVICE DELIVERS IT.
    // With the defibrillator in PACER mode, capture is re-evaluated whenever the output, rate,
    // threshold or underlying rhythm changes:
    //   * ELECTRICAL capture at the threshold: every spike is followed by a broad complex, the
    //     monitor rate becomes the paced rate — but the circulation has not improved yet.
    //   * MECHANICAL capture ~10 mA above it: a palpable pulse at the paced rate, BP and
    //     saturation improve.
    // Demand mode is inhibited while the patient's own rate is at or above the set rate.
    // Losing capture (or leaving PACER mode) restores the underlying rhythm and obs.
    // =====================================================================================
    var MECHANICAL_MARGIN_MA = 10;
    useEffect(function () {
      if (isMonitorMode) return;
      var cur = stateRef.current;
      if (cur.isFinished) return; // the debrief is frozen at Finish
      // Capture waits for START like every other effect on the patient (re-evaluated then).
      if (notStarted && notStarted(cur)) return;
      var pacing = cur.pacing || {};
      var mode = cur.defib && cur.defib.mode;
      var pacer = cur.remotePacerState || {};
      var rate = Number(pacer.rate) || 0,
        output = Number(pacer.output) || 0;
      var demand = pacer.demand !== false;
      // The facilitator changed the rhythm under an established capture: that is the new
      // underlying rhythm, and the capture has to be earned again.
      if (pacing.electrical && RG.canonical(cur.rhythm) !== 'Paced') {
        dispatch({
          type: 'SET_PACING',
          payload: {
            electrical: false,
            mechanical: false,
            underlying: null,
            pre: null
          }
        });
        return;
      }
      var underlying = pacing.electrical ? pacing.underlying : RG.canonical(cur.rhythm);
      var ownRate = pacing.electrical ? pacing.pre && pacing.pre.hr || 0 : cur.vitals && cur.vitals.hr || 0;
      var threshold = Number(cur.pacingThreshold) || 70;
      var on = mode === 'pacer' && rate > 0 && output > 0;
      var capable = RG.PACEABLE.indexOf(RG.canonical(underlying)) !== -1;
      var inhibited = demand && ownRate >= rate;
      var electrical = on && capable && !inhibited && output >= threshold;
      var mechanical = electrical && output >= threshold + MECHANICAL_MARGIN_MA;
      if (electrical && !pacing.electrical) {
        var v = cur.baseVitals || {};
        var pre = {
          hr: v.hr,
          bpSys: v.bpSys,
          bpDia: v.bpDia,
          spO2: v.spO2
        };
        dispatch({
          type: 'SET_PACING',
          payload: {
            electrical: true,
            mechanical: false,
            underlying: RG.canonical(underlying),
            pre: pre
          }
        });
        changeRhythm('Paced', 'pacing', {
          note: "electrical capture at ".concat(output, "mA, ").concat(rate, "/min")
        });
        dispatch({
          type: 'UPDATE_VITALS',
          payload: {
            hr: rate
          }
        });
        addLogEntry("Pacing: ELECTRICAL capture at ".concat(output, "mA, ").concat(rate, "/min. Confirm MECHANICAL capture \u2014 a palpable pulse at the paced rate. Pacing hurts: give analgesia and sedation."), 'success');
        // After this render, so the step's rhythm follows the capture rather than racing it.
        setTimeout(function () {
          return defibStepTrigger('capture');
        }, 0);
        return;
      }
      if (!electrical && pacing.electrical) {
        var _pre = pacing.pre || {};
        dispatch({
          type: 'SET_PACING',
          payload: {
            electrical: false,
            mechanical: false,
            underlying: null,
            pre: null
          }
        });
        // Three different reasons, three different messages: the pacer was switched off (or
        // its output/rate set to 0, as the defib tablet does when it loads or closes), demand
        // pacing is inhibited by the patient's own faster rate, or the output fell below
        // threshold. Only the last is a true loss of capture.
        var why = !on ? 'stopped' : inhibited ? 'inhibited' : 'lost';
        changeRhythm(pacing.underlying || 'Sinus Rhythm', 'pacing', {
          note: why === 'stopped' ? 'pacing stopped' : why === 'inhibited' ? 'demand pacing inhibited' : 'capture lost'
        });
        var restore = {};
        ['hr', 'bpSys', 'bpDia', 'spO2'].forEach(function (k) {
          if (Number.isFinite(Number(_pre[k]))) restore[k] = Number(_pre[k]);
        });
        dispatch({
          type: 'UPDATE_VITALS',
          payload: restore
        });
        if (why === 'stopped') addLogEntry('Pacing stopped — back to the underlying rhythm.', 'warning');else if (why === 'inhibited') addLogEntry("Pacing: demand mode INHIBITED \u2014 the patient's own rate (".concat(Math.round(ownRate), "/min) is at or above the set rate (").concat(rate, "/min), so the pacer is not firing. Raise the rate to pace."), 'info');else addLogEntry("Pacing: capture LOST (".concat(output, "mA < threshold). Increase the output."), 'warning', true);
        return;
      }
      if (!electrical) return;
      var updates = {};
      if (Number((cur.baseVitals || {}).hr) !== rate) updates.hr = rate;
      if (mechanical && !pacing.mechanical) {
        var _pre2 = pacing.pre || {};
        updates.bpSys = Math.max((Number(_pre2.bpSys) || 70) + 25, 95);
        updates.bpDia = Math.max((Number(_pre2.bpDia) || 40) + 15, 55);
        if (Number(_pre2.spO2) > 0) updates.spO2 = Math.max(Number(_pre2.spO2), 95);
        dispatch({
          type: 'SET_PACING',
          payload: {
            mechanical: true
          }
        });
        addLogEntry("Pacing: MECHANICAL capture at ".concat(output, "mA \u2014 pulse palpable at ").concat(rate, "/min, blood pressure improving."), 'success');
      } else if (!mechanical && pacing.mechanical) {
        var _pre3 = pacing.pre || {};
        ['bpSys', 'bpDia', 'spO2'].forEach(function (k) {
          if (Number.isFinite(Number(_pre3[k]))) updates[k] = Number(_pre3[k]);
        });
        dispatch({
          type: 'SET_PACING',
          payload: {
            mechanical: false
          }
        });
        addLogEntry("Pacing: mechanical capture lost at ".concat(output, "mA \u2014 electrical capture only, no pulse at the paced rate."), 'warning', true);
      }
      if (Object.keys(updates).length) dispatch({
        type: 'UPDATE_VITALS',
        payload: updates
      });
    }, [isMonitorMode, state.defib && state.defib.mode, state.remotePacerState, state.pacingThreshold, state.rhythm, state.pacing, state.isRunning]);
    return {
      analyseRhythm: analyseRhythm,
      applyCardioversion: applyCardioversion,
      applyDrugConversion: applyDrugConversion,
      applyShockOutcome: applyShockOutcome,
      defibWeight: defibWeight,
      deliverShock: deliverShock,
      drugOnBoard: drugOnBoard,
      goToDefibStep: goToDefibStep,
      initCharge: initCharge,
      recommendedShockEnergy: recommendedShockEnergy,
      refibTimerRef: refibTimerRef,
      setDefibEnergy: setDefibEnergy,
      setDefibMode: setDefibMode,
      setQueuedRhythm: setQueuedRhythm,
      shockPolicy: shockPolicy,
      stepsRunning: stepsRunning,
      toggleCPR: toggleCPR,
      toggleDefibSync: toggleDefibSync
    };
  };
  window.__EngineDefib = {
    useDefib: useDefib
  };
})();